import React, { useState, useEffect } from 'react';
import {
  Modal,
  Input,
  Button,
  Space,
  message,
  Card,
  Spin,
  Image,
  Empty,
} from 'antd';
import {
  ScanOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useIntl } from 'umi';
import { request } from '@umijs/max';

interface PickingModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface OrderItem {
  offerId: string;
  itemUniqueNo: number;
  subject: string;
  subjectTrans: string;
  imageUrl: string;
  quantity: number;
  skuAttributes?: Array<{
    attributeId: number;
    attributeValue: string;
  }>;
}

interface PackageInfo {
  trackingNumber: string;
  rackNumber: string;
  items: OrderItem[];
}

interface PickingOrder {
  orderId: string;
  orderNumber: string;
  progressStatus: string;
  packages: PackageInfo[];
}

interface PickingReadyResponse {
  status: string;
  statusCode: number;
  message: string;
  data: {
    orders: PickingOrder[];
  };
  timestamp: string;
}

const PickingModal: React.FC<PickingModalProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const intl = useIntl();
  
  // State
  const [trackingNumber, setTrackingNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pickingData, setPickingData] = useState<PickingOrder | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<PackageInfo | null>(null);
  const [scanAttempted, setScanAttempted] = useState(false);

  // Fetch picking ready orders when modal opens
  useEffect(() => {
    if (visible) {
      fetchPickingReadyOrders();
    }
  }, [visible]);

  const fetchPickingReadyOrders = async () => {
    setLoading(true);
    try {
      const response = await request<PickingReadyResponse>(
        '/pda/workflow/picking/ready',
        {
          method: 'GET',
        }
      );

      if (response.statusCode === 200 && response.data) {
        if (response.data.orders.length === 0) {
          message.info(
            intl.formatMessage({ id: 'pages.orders.picking.noOrdersReady' }),
          );
        }
      } else {
        message.error(
          response.message ||
            intl.formatMessage({ id: 'pages.orders.picking.fetchFailed' }),
        );
      }
    } catch (error: any) {
      // console.error('Error fetching picking ready orders:', error);
      message.error(
        error?.response?.data?.message ||
          intl.formatMessage({ id: 'pages.orders.picking.fetchFailed' }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleScan = async () => {
    if (!trackingNumber.trim()) {
      message.warning(
        intl.formatMessage({ id: 'pages.orders.picking.enterTrackingNumber' }),
      );
      return;
    }

    setScanAttempted(true);
    setLoading(true);
    try {
      const response = await request<PickingReadyResponse>(
        '/pda/workflow/picking/ready',
        {
          method: 'GET',
        }
      );

      if (response.statusCode === 200 && response.data) {
        const orders = response.data.orders;
        
        if (orders.length === 0) {
          message.warning(
            intl.formatMessage({
              id: 'pages.orders.picking.noMatchedProduct',
            }),
          );
          setPickingData(null);
          setSelectedPackage(null);
          return;
        }

        // Find the order and package that matches the tracking number
        let foundOrder: PickingOrder | null = null;
        let foundPackage: PackageInfo | null = null;

        for (const order of orders) {
          const pkg = order.packages.find(
            (p) => p.trackingNumber === trackingNumber.trim()
          );
          if (pkg) {
            foundOrder = order;
            foundPackage = pkg;
            break;
          }
        }

        if (foundOrder && foundPackage) {
          setPickingData(foundOrder);
          setSelectedPackage(foundPackage);
          message.success(
            intl.formatMessage({ id: 'pages.orders.picking.orderFound' }),
          );
        } else {
          message.warning(
            intl.formatMessage({
              id: 'pages.orders.picking.noMatchedProductWithTracking',
            }),
          );
          setPickingData(null);
          setSelectedPackage(null);
        }
      } else {
        message.error(
          response.message ||
            intl.formatMessage({ id: 'pages.orders.picking.scanFailed' }),
        );
      }
    } catch (error: any) {
      // console.error('Scan error:', error);
      message.error(
        error?.response?.data?.message ||
          intl.formatMessage({ id: 'pages.orders.picking.scanFailed' }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePick = async () => {
    if (!pickingData || !selectedPackage) {
      message.warning(
        intl.formatMessage({ id: 'pages.orders.picking.noOrderSelected' }),
      );
      return;
    }

    if (!trackingNumber.trim()) {
      message.warning(
        intl.formatMessage({ id: 'pages.orders.picking.enterTrackingNumber' }),
      );
      return;
    }

    setSubmitting(true);
    try {
      const response = await request<{
        status: string;
        statusCode: number;
        message?: string;
        data?: any;
      }>('/pda/workflow/pick', {
        method: 'POST',
        data: {
          parentOrderId: pickingData.orderId,
          trackingNumber: trackingNumber.trim(),
          locationCode: selectedPackage.rackNumber,
        },
      });

      if (response?.statusCode === 200 || response?.status === 'success') {
        message.success(
          response?.message ||
            intl.formatMessage({ id: 'pages.orders.picking.completed' }),
        );
        resetForm();
        onSuccess?.();
        onClose();
      } else {
        message.error(
          response?.message ||
            intl.formatMessage({ id: 'pages.orders.picking.completeFailed' }),
        );
      }
    } catch (error: any) {
      // console.error('Error completing picking:', error);
      message.error(
        error?.response?.data?.message ||
          intl.formatMessage({ id: 'pages.orders.picking.completeFailed' }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setTrackingNumber('');
    setPickingData(null);
    setSelectedPackage(null);
    setScanAttempted(false);
  };

  return (
    <Modal
      title={
        <div
          style={{
            background: 'linear-gradient(90deg, #52c41a 0%, #73d13d 100%)',
            color: 'white',
            padding: '10px 16px',
            margin: '-20px -24px 20px -24px',
            fontSize: 18,
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span style={{ flex: 1, textAlign: 'center' }}>
            {intl.formatMessage({ id: 'pages.orders.picking.title' })}
          </span>
          {!pickingData && (
            <CloseOutlined
              onClick={handleCancel}
              style={{ cursor: 'pointer', fontSize: 18 }}
            />
          )}
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={700}
      closeIcon={null}
      styles={{
        header: { padding: 0, border: 'none', marginBottom: 0 },
        body: { padding: '24px' },
      }}
    >
      <Spin spinning={loading || submitting}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* Tracking Number Input */}
          <div>
            <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
              {intl.formatMessage({
                id: 'pages.orders.picking.trackingNumber',
              })}
            </div>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                value={trackingNumber}
                onChange={(e) => {
                  setTrackingNumber(e.target.value);
                  setScanAttempted(false);
                }}
                onPressEnter={handleScan}
                size="large"
                placeholder={intl.formatMessage({
                  id: 'pages.orders.picking.trackingNumberPlaceholder',
                })}
                disabled={!!pickingData}
              />
              <Button
                type="primary"
                size="large"
                icon={<ScanOutlined />}
                onClick={handleScan}
                loading={loading}
                disabled={!!pickingData}
                style={{
                  backgroundColor: '#52c41a',
                  borderColor: '#52c41a',
                }}
              >
                {intl.formatMessage({ id: 'pages.orders.picking.scan' })}
              </Button>
            </Space.Compact>
          </div>

          {/* Picking Results - show product information when scan matches */}
          {pickingData && selectedPackage ? (
            <Card>
              {/* Order Information */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 'bold', fontSize: 16, marginBottom: 8 }}>
                  {intl.formatMessage({
                    id: 'pages.orders.picking.orderNumber',
                  })}{' '}
                  {pickingData.orderNumber}
                </div>
                <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 12, color: '#52c41a' }}>
                  {intl.formatMessage({
                    id: 'pages.orders.picking.rackNumber',
                  })}{' '}
                  {selectedPackage.rackNumber}
                </div>
              </div>

              {/* Product Information */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 12, fontSize: 15 }}>
                  {intl.formatMessage({
                    id: 'pages.orders.picking.productInformation',
                  })}
                </div>
                {selectedPackage.items.map((item, idx) => (
                  <Card
                    key={item.itemUniqueNo != null ? item.itemUniqueNo : idx}
                    size="small"
                    style={{ marginBottom: 8 }}
                  >
                    <div style={{ display: 'flex', gap: 12 }}>
                      <Image
                        src={item.imageUrl}
                        alt={item.subjectTrans || item.subject}
                        width={80}
                        height={80}
                        style={{ objectFit: 'cover', borderRadius: 4 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                          {item.subjectTrans || item.subject}
                        </div>
                        {item.subject && item.subject !== item.subjectTrans && (
                          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                            {item.subject}
                          </div>
                        )}
                        <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                          {intl.formatMessage({
                            id: 'pages.orders.picking.quantity',
                          })}
                          : {item.quantity}
                        </div>
                        {item.skuAttributes && item.skuAttributes.length > 0 && (
                          <div style={{ fontSize: 12, color: '#666' }}>
                            {item.skuAttributes.map((attr, attrIdx) => (
                              <span key={attrIdx}>
                                {attr.attributeValue}
                                {attrIdx < item.skuAttributes!.length - 1 && ', '}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Action Buttons */}
              <Space style={{ width: '100%' }}>
                <Button onClick={() => {
                  setPickingData(null);
                  setSelectedPackage(null);
                }}>
                  {intl.formatMessage({ id: 'pages.common.cancel' })}
                </Button>
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={handlePick}
                  loading={submitting}
                  style={{
                    backgroundColor: '#52c41a',
                    borderColor: '#52c41a',
                  }}
                >
                  {intl.formatMessage({ id: 'pages.orders.picking.pick' })}
                </Button>
              </Space>
            </Card>
          ) : scanAttempted && !loading && !pickingData ? (
            <Empty
              description={intl.formatMessage({
                id: 'pages.orders.picking.noMatchedProduct',
              })}
            />
          ) : null}
        </Space>
      </Spin>
    </Modal>
  );
};

export default PickingModal;
